import type { MutableRefObject } from 'react';
import type { BoxProps } from '@mui/material/Box';
import type { NavSectionProps } from 'src/components/nav-section';
import type { CommandPaletteItem } from 'src/search/command-palette';
import type { ProviderSearchResult } from 'src/search/providers/types';

import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { varAlpha } from 'minimal-shared/utils';
import { useBoolean } from 'minimal-shared/hooks';
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import SvgIcon from '@mui/material/SvgIcon';
import Skeleton from '@mui/material/Skeleton';
import MenuList from '@mui/material/MenuList';
import { useTheme } from '@mui/material/styles';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog, { dialogClasses } from '@mui/material/Dialog';
import MenuItem, { menuItemClasses } from '@mui/material/MenuItem';

import { useRouter, usePathname } from 'src/routes/hooks';

import { getConfig } from 'src/global-config';
import { isWebGPUSupported } from 'src/search/webllm/client';
import { runJobsAndCandidatesSearch } from 'src/search/utils';
import { buildSearchProviders } from 'src/search/providers/registry';
import { useGlobalJobsSearchData } from 'src/search/hooks/useGlobalJobsSearchData';
import {
  buildCommandPaletteItems,
  filterCommandPaletteItems,
  providerSearchResultToCommandItem,
} from 'src/search/command-palette';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';
import { SearchNotFound } from 'src/components/search-not-found';

import { ResultItem } from './result-item';

// ----------------------------------------------------------------------

export type SearchbarProps = BoxProps & {
  data?: NavSectionProps['data'];
};

const SEARCH_METHOD: 'ai' | 'fuse' = 'fuse';

function renderCommandRow(
  item: CommandPaletteItem,
  globalIndex: number,
  activeIndex: number,
  open: boolean,
  searchQuery: string,
  listItemRefs: MutableRefObject<(HTMLLIElement | null)[]>,
  setActiveIndex: (i: number | ((prev: number) => number)) => void,
  handleClose: () => void
) {
  const secondaryText = [item.subtitle, item.path].filter(Boolean).join(' · ');
  const partsTitle = parse(item.title, match(item.title, searchQuery));
  const partsSecondary = parse(secondaryText, match(secondaryText, searchQuery));
  const labels = item.group.includes('·')
    ? item.group.split('·').map((s) => s.trim())
    : item.group.split('.');

  return (
    <MenuItem
      disableRipple
      key={`${item.source}-${item.path}-${item.title}-${globalIndex}`}
      ref={(el) => {
        listItemRefs.current[globalIndex] = el;
      }}
      selected={globalIndex === activeIndex}
      onMouseEnter={() => {
        if (!open) return;
        setActiveIndex(globalIndex);
      }}
      sx={{
        p: 0,
        mb: 0,
        bgcolor: (theme) =>
          globalIndex === activeIndex
            ? varAlpha(theme.vars.palette.primary.mainChannel, 0.08)
            : undefined,
        '&:hover': { bgcolor: 'transparent' },
      }}
    >
      <ResultItem
        path={partsSecondary}
        title={partsTitle}
        href={item.path}
        labels={labels.filter(Boolean)}
        source={item.source}
        onClick={handleClose}
      />
    </MenuItem>
  );
}

export function Searchbar({ data: navItems = [], sx, ...other }: SearchbarProps) {
  const theme = useTheme();
  const router = useRouter();
  const pathname = usePathname();

  const { value: open, onFalse: onClose, onTrue: onOpen, onToggle } = useBoolean();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [semanticJobItems, setSemanticJobItems] = useState<ProviderSearchResult[]>([]);
  const [semanticCandidateItems, setSemanticCandidateItems] = useState<ProviderSearchResult[]>(
    []
  );
  const [isSemanticLoading, setIsSemanticLoading] = useState(false);
  const [semanticError, setSemanticError] = useState<string | null>(null);
  const [engineReady, setEngineReady] = useState(false);
  const [semanticBootTimedOut, setSemanticBootTimedOut] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const activeIndexRef = useRef(0);
  const requestVersionRef = useRef(0);
  const listItemRefs = useRef<(HTMLLIElement | null)[]>([]);
  const { jobsForLlm } = useGlobalJobsSearchData();
  const config = getConfig();
  const clearSemanticState = useCallback(() => {
    setSemanticJobItems((prev) => (prev.length ? [] : prev));
    setSemanticCandidateItems((prev) => (prev.length ? [] : prev));
    setIsSemanticLoading((prev) => (prev ? false : prev));
    setSemanticError((prev) => (prev ? null : prev));
  }, []);

  const semanticUiEnabled = isWebGPUSupported() && engineReady;
  const semanticSearchEnabled = SEARCH_METHOD === 'fuse' || semanticUiEnabled;

  // useEffect(() => {
  //   if (!isWebGPUSupported()) {
  //     setEngineReady(false);
  //     return () => {};
  //   }

  //   let cancelled = false;
  //   void initWebLLM()
  //     .then(() => {
  //       if (!cancelled) setEngineReady(true);
  //     })
  //     .catch(() => {
  //       if (!cancelled) setEngineReady(false);
  //     });

  //   return () => {
  //     cancelled = true;
  //   };
  // }, []);

  useEffect(() => {
    if (semanticUiEnabled) {
      setSemanticBootTimedOut(false);
      return () => {};
    }

    const timeoutId = window.setTimeout(() => {
      setSemanticBootTimedOut(true);
    }, 60_000);

    return () => window.clearTimeout(timeoutId);
  }, [semanticUiEnabled]);

  const handleClose = useCallback(() => {
    onClose();
    setSearchQuery('');
    setDebouncedQuery('');
    clearSemanticState();
    setActiveIndex(0);
  }, [clearSemanticState, onClose]);

  const paletteItems = useMemo(() => buildCommandPaletteItems(navItems), [navItems]);
  const providers = useMemo(() => buildSearchProviders({ jobs: jobsForLlm }), [jobsForLlm]);

  const navFiltered = useMemo(
    () => filterCommandPaletteItems(paletteItems, searchQuery),
    [paletteItems, searchQuery]
  );

  const flatSelectableItems = useMemo(() => {
    const jobCmd = semanticJobItems.map(providerSearchResultToCommandItem);
    const candCmd = semanticCandidateItems.map(providerSearchResultToCommandItem);
    if (!semanticSearchEnabled) {
      return navFiltered;
    }
    return [...navFiltered, ...jobCmd, ...candCmd];
  }, [navFiltered, semanticJobItems, semanticCandidateItems, semanticSearchEnabled]);

  const handleKeyDownGlobal = useCallback(
    (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'h' && event.ctrlKey) {
        event.preventDefault();
        onToggle();
        setSearchQuery('');
        setActiveIndex(0);
      }
    },
    [onToggle]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDownGlobal);

    return () => {
      window.removeEventListener('keydown', handleKeyDownGlobal);
    };
  }, [handleKeyDownGlobal]);

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  useEffect(() => {
    setActiveIndex(0);
  }, [searchQuery, open]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedQuery(searchQuery.trim());
    }, 500);
    return () => window.clearTimeout(timeoutId);
  }, [searchQuery]);

  useEffect(() => {
    const trimmed = debouncedQuery.trim();
    if (!open || !trimmed) {
      clearSemanticState();
      return;
    }

    if (!semanticSearchEnabled) {
      clearSemanticState();
      return;
    }

    const requestVersion = ++requestVersionRef.current;
    const shouldCancel = () => requestVersionRef.current !== requestVersion;

    const runSemanticSearch = async () => {
      setIsSemanticLoading(true);
      setSemanticError(null);
      const startedAt = performance.now();

      try {
        const { jobs, candidates } = await runJobsAndCandidatesSearch(
          trimmed,
          providers,
          SEARCH_METHOD,
          shouldCancel
        );
        if (shouldCancel()) {
          return;
        }
        const elapsed = Math.round(performance.now() - startedAt);

        if (config.appMode === 'development' || config.appMode === 'local') {
          console.debug('[global-search]', {
            query: trimmed,
            elapsedMs: elapsed,
            jobCount: jobs.length,
            candidateCount: candidates.length,
            jobs,
            candidates,
          });
        }

        if (requestVersionRef.current === requestVersion) {
          setSemanticJobItems(jobs);
          setSemanticCandidateItems(candidates);
          setSemanticError(null);
        }
      } catch (error) {
        if (shouldCancel()) {
          return;
        }
        if (config.appMode === 'development' || config.appMode === 'local') {
          console.debug('[global-search] semantic search failed', error);
        }
        if (requestVersionRef.current === requestVersion) {
          setSemanticJobItems([]);
          setSemanticCandidateItems([]);
          setSemanticError('semantic-unavailable');
        }
      } finally {
        if (requestVersionRef.current === requestVersion) {
          setIsSemanticLoading(false);
        }
      }
    };

    runSemanticSearch();
  }, [debouncedQuery, open, providers, semanticSearchEnabled, config.appMode, clearSemanticState]);

  useEffect(() => {
    if (activeIndex >= flatSelectableItems.length) {
      setActiveIndex(Math.max(0, flatSelectableItems.length - 1));
    }
  }, [flatSelectableItems.length, activeIndex]);

  useEffect(() => {
    if (!open || !flatSelectableItems.length) {
      return () => {};
    }

    const onPaletteKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, flatSelectableItems.length - 1));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (event.key === 'Enter') {
        const item = flatSelectableItems[activeIndexRef.current];
        if (item) {
          event.preventDefault();
          handleClose();
          if (item.path !== pathname) {
            //close dialogue and then navigate
            window.setTimeout(() => {
              router.push(item.path);
            }, 500);
          }
        }
      }
    };

    window.addEventListener('keydown', onPaletteKeyDown);
    return () => window.removeEventListener('keydown', onPaletteKeyDown);
  }, [open, flatSelectableItems, router, handleClose, pathname]);

  useEffect(() => {
    const el = listItemRefs.current[activeIndex];
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [activeIndex, flatSelectableItems]);

  const handleSearch = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setSearchQuery(event.target.value);
  }, []);

  const notFound =
    Boolean(searchQuery.trim()) && !isSemanticLoading && flatSelectableItems.length === 0;
  const semanticStatus = useMemo(() => {
    if (SEARCH_METHOD === 'fuse') {
      if (!isSemanticLoading) {
        return null;
      }
      return {
        color: 'text.secondary',
        line1: 'Searching for jobs...',
        line2: '',
      };
    }

    if (semanticUiEnabled) {
      if (!isSemanticLoading && !semanticError) {
        return null;
      }
      return {
        color: semanticError ? 'warning.main' : 'text.secondary',
        line1: semanticError
          ? 'Showing navigation only. Jobs and candidates search is unavailable.'
          : 'Searching for jobs and candidates…',
        line2: '',
      };
    }

    if (semanticBootTimedOut) {
      return {
        color: 'warning.main',
        line1: 'AI search is currently unavailable',
        line2: 'Showing navigation results instead',
      };
    }

    return {
      color: 'text.secondary',
      line1: 'Booting up the Hello Recruiter AI engine…',
      line2: 'Search is currently limited to navigation items (~60 seconds)',
    };
  }, [semanticUiEnabled, isSemanticLoading, semanticError, semanticBootTimedOut]);

  const jobCommandItems = useMemo(
    () => semanticJobItems.map(providerSearchResultToCommandItem),
    [semanticJobItems]
  );
  const candidateCommandItems = useMemo(
    () => semanticCandidateItems.map(providerSearchResultToCommandItem),
    [semanticCandidateItems]
  );

  const navCount = navFiltered.length;

  const renderButton = () => (
    <Box
      onClick={() => {
        onOpen();
      }}
      sx={[
        () => ({
          display: 'flex',
          alignItems: 'center',
          pr: { sm: 1 },
          borderRadius: { sm: 1.5 },
          cursor: { sm: 'pointer' },
          bgcolor: { sm: varAlpha(theme.vars.palette.grey['500Channel'], 0.08) },
          transition: theme.transitions.create('background-color', {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.shortest,
          }),
          '&:hover': {
            bgcolor: {
              sm: varAlpha(theme.vars.palette.grey['500Channel'], 0.16),
            },
          },
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      <IconButton disableRipple>
        <SvgIcon sx={{ width: 20, height: 20 }}>
          <path
            fill="currentColor"
            d="m20.71 19.29l-3.4-3.39A7.92 7.92 0 0 0 19 11a8 8 0 1 0-8 8a7.92 7.92 0 0 0 4.9-1.69l3.39 3.4a1 1 0 0 0 1.42 0a1 1 0 0 0 0-1.42M5 11a6 6 0 1 1 6 6a6 6 0 0 1-6-6"
          />
        </SvgIcon>
      </IconButton>

      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          ml: 1,
          width: '100%',
          '&::before': {
            content: {
              xs: '"Search"',
              sm: '"Find jobs or navigate pages..."',
              md: '"Find jobs or navigate pages..."',
            },
          },
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}
      />

      <Label
        sx={{
          color: 'grey.800',
          cursor: 'inherit',
          bgcolor: 'common.white',
          fontSize: theme.typography.pxToRem(12),
          boxShadow: theme.vars.customShadows.z1,
          display: { xs: 'none', sm: 'inline-flex' },
          ml: { xs: 0, sm: 1, md: 2, lg: 3 },
          p: 1
        }}
      >
        Ctrl+H
      </Label>
    </Box>
  );

  const sectionHeaderSx = {
    display: 'block',
    mt: 2,
    mb: 1,
    color: 'text.secondary',
    letterSpacing: 0.5,
  } as const;

  const menuListSx = {
    [`& .${menuItemClasses.root}`]: {
      p: 0,
      mb: 0,
      '&:hover': { bgcolor: 'transparent' },
    },
  };

  return (
    <>
      {renderButton()}

      <Dialog
        fullWidth
        closeAfterTransition
        maxWidth="sm"
        keepMounted={false}
        open={open}
        onClose={() => {
          handleClose();
        }}
        transitionDuration={{ enter: theme.transitions.duration.shortest, exit: 0 }}
        PaperProps={{ sx: { mt: 15, overflow: 'unset', pointerEvents: open ? 'auto' : 'none' } }}
        sx={[
          () => ({
            [`& .${dialogClasses.container}`]: {
              alignItems: 'flex-start',
            },
          }),
        ]}
      >
        <Box sx={{ p: 3, borderBottom: `solid 1px ${theme.vars.palette.divider}` }}>
          <InputBase
            fullWidth
            autoFocus={open}
            placeholder="Search..."
            value={searchQuery}
            onChange={handleSearch}
            startAdornment={
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" width={24} sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            }
            endAdornment={<Label sx={{ letterSpacing: 1, color: 'text.secondary' }}>esc</Label>}
            inputProps={{ id: 'search-input', sx: { typography: 'h6' } }}
          />
          {semanticStatus && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ display: 'block', color: semanticStatus.color }}>
                {semanticStatus.line1}
              </Typography>
              {semanticStatus.line2 && (
                <Typography variant="caption" sx={{ display: 'block', color: semanticStatus.color }}>
                  {semanticStatus.line2}
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {open &&
          (notFound ? (
            <SearchNotFound query={searchQuery} sx={{ py: 15 }} />
          ) : (
            <Scrollbar
              sx={{
                px: 3,
                pb: 3,
                pt: 2,
                height: 400,
              }}
            >
              <Typography variant="overline" sx={{ ...sectionHeaderSx, mt: 0 }}>
                Navigation
              </Typography>
              <MenuList disablePadding sx={menuListSx}>
                {navFiltered.map((item, index) =>
                  renderCommandRow(
                    item,
                    index,
                    activeIndex,
                    open,
                    searchQuery,
                    listItemRefs,
                    setActiveIndex,
                    handleClose
                  )
                )}
              </MenuList>

              {semanticSearchEnabled && (
                <>
                  <Typography variant="overline" sx={sectionHeaderSx}>
                    Jobs
                  </Typography>
                  <MenuList disablePadding sx={menuListSx}>
                    {isSemanticLoading ? (
                      Array.from({ length: 3 }).map((_, index) => (
                        <Box key={`job-skeleton-${index}`} sx={{ py: 0.5 }}>
                          <Skeleton variant="rectangular" width="100%" height={56} />
                        </Box>
                      ))
                    ) : jobCommandItems.length === 0 && debouncedQuery.trim() ? (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 1, px: 0.5 }}>
                        No matching jobs.
                      </Typography>
                    ) : (
                      jobCommandItems.map((item, index) =>
                        renderCommandRow(
                          item,
                          navCount + index,
                          activeIndex,
                          open,
                          searchQuery,
                          listItemRefs,
                          setActiveIndex,
                          handleClose
                        )
                      )
                    )}
                  </MenuList>

                  {/* <Typography variant="overline" sx={sectionHeaderSx}>
                    Candidates
                  </Typography>
                  <MenuList disablePadding sx={menuListSx}>
                    {isSemanticLoading ? (
                      Array.from({ length: 3 }).map((_, index) => (
                        <Box key={`candidate-skeleton-${index}`} sx={{ py: 0.5 }}>
                          <Skeleton variant="rectangular" width="100%" height={56} />
                        </Box>
                      ))
                    ) : candidateCommandItems.length === 0 && debouncedQuery.trim() ? (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 1, px: 0.5 }}>
                        No matching candidates.
                      </Typography>
                    ) : (
                      candidateCommandItems.map((item, index) =>
                        renderCommandRow(
                          item,
                          navCount + jobCommandItems.length + index,
                          activeIndex,
                          open,
                          searchQuery,
                          listItemRefs,
                          setActiveIndex,
                          handleClose
                        )
                      )
                    )}
                  </MenuList> */}
                </>
              )}
            </Scrollbar>
          ))}
      </Dialog>
    </>
  );
}
